import { useCallback, useEffect, useRef, useState } from 'react';
import type { WorkflowStep } from '../WorkflowProgressAccordion';

/** The `step` event shape streamed by the runner — identical for execute and build. */
interface StreamStepEvent {
  step?: { action?: string; message?: string; durationMs?: number; skipped?: boolean };
  log?: string;
  screenshot?: string;
}

/** Map a runner step event onto the phase shape the workflow accordion renders. */
export function toConversePhase(event: StreamStepEvent, fallbackLabel: string): WorkflowStep {
  const { step, log, screenshot } = event;
  return {
    type: step?.action || 'step',
    message: log || step?.message || step?.action || fallbackLabel,
    duration_sec: step?.durationMs != null ? step.durationMs / 1000 : undefined,
    ...(step?.skipped ? { skipped: true } : {}),
    ...(screenshot ? { screenshot } : {}),
  };
}

export function useWebAgentConverse() {
  const [converseMessages, setConverseMessages] = useState<any[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const activeIndexRef = useRef<number | null>(null);
  const sendingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  /** Patch the in-flight agent message (the one holding live phases / the final summary). */
  const patchActive = useCallback((patch: (msg: any) => any) => {
    setConverseMessages((prev) => {
      const idx = activeIndexRef.current;
      if (idx == null || idx < 0 || idx >= prev.length) return prev;
      const updated = [...prev];
      updated[idx] = patch(updated[idx]);
      return updated;
    });
  }, []);

  const appendPhase = useCallback((phase: WorkflowStep) => {
    patchActive((msg) => ({ ...msg, phases: [...(msg.phases ?? []), phase] }));
  }, [patchActive]);

  /** Append the user turn plus an empty agent turn, then open a fresh stream; returns its abort signal. */
  const beginTurn = useCallback((userTurn: any, agentTurn: any): AbortSignal => {
    setConverseMessages((prev) => {
      activeIndexRef.current = prev.length + 1;
      return [...prev, userTurn, agentTurn];
    });
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    sendingRef.current = true;
    setIsStreaming(true);
    return controller.signal;
  }, []);

  const endTurn = useCallback(() => {
    sendingRef.current = false;
    setIsStreaming(false);
  }, []);

  return { converseMessages, isStreaming, sendingRef, patchActive, appendPhase, beginTurn, endTurn };
}
